import { DOMSerializer, type Node } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";
import { useCallback } from "react";

import { schema } from "@moment/prosemirror-schema";

/**
 * Convert a ProseMirror node to plain text, handling atom nodes (whose text will not necesssarily
 * be serialized) appropriately.
 */
function nodeToPlainText(node: Node): string {
	// Handle custom inline nodes that have no text content
	switch (node.type.name) {
		case "pull_request": {
			const url = node.attrs["url"] as string;
			return url;
		}
		case "code_snippet": {
			const url = node.attrs["url"] as string;
			return url;
		}
		case "code_snippet_block": {
			const url = node.attrs["url"] as string;
			return url;
		}
		default: {
			// For all other nodes, recursively get text content
			if (node.isText) {
				return node.text || "";
			}
			let text = "";
			node.forEach((child) => {
				text += nodeToPlainText(child);
			});
			// Add newlines for block-level nodes
			if (node.isBlock && text && !text.endsWith("\n")) {
				text += "\n";
			}
			return text;
		}
	}
}

/**
 * Custom copy handler for ProseMirror that adds text/markdown and text/plain
 * clipboard data in addition to the default text/html.
 */
export const useEaselProseMirrorCopyHandler = () => {
	return useCallback((view: EditorView, event: ClipboardEvent) => {
		const { state } = view;
		const { selection } = state;

		// Get the selected content
		const slice = selection.content();
		const fragment = slice.content;

		// If nothing is selected, let the default behavior handle it
		if (fragment.size === 0) {
			return false;
		}

		if (!event.clipboardData) {
			return false;
		}

		try {
			// 1. Serialize to HTML (using ProseMirror's DOM serializer)
			const domSerializer = DOMSerializer.fromSchema(schema);
			const div = document.createElement("div");
			const domFragment = domSerializer.serializeFragment(fragment);
			div.appendChild(domFragment);
			const html = div.innerHTML;

			// 2. Serialize to plain text
			// For custom nodes like pull_request and code_snippet, extract meaningful text
			const plainTextParts: string[] = [];
			fragment.forEach((node: Node) => {
				plainTextParts.push(nodeToPlainText(node));
			});
			const plainText = plainTextParts.join("");

			// Set all formats on the clipboard
			event.clipboardData.setData("text/html", html);
			event.clipboardData.setData("text/plain", plainText);

			// Prevent the default copy behavior since we've handled it
			event.preventDefault();
			return true;
		} catch (error) {
			console.error("Error copying content:", error);
			return false;
		}
	}, []);
};
